import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	useGetCopywritingSettingsQuery,
	usePatchCopywritingSettingsMutation,
} from "@/pages/Private/redux/copywritingSettings/copywritingSettings.api";
import { useUpdateInSettingsMutation } from "@/pages/Private/redux/company/company.api";
import { useAppSelector } from "@/redux/hooks";
import { copywritingSettingsSelector } from "@/pages/Private/redux/copywritingSettings/copywritingSettings.slice";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { LoadingOverlay } from "@/components/LoadingOverlay/LoadingOverlay";
import { Accordion, AccordionItem } from "@/components/Accordion/Accordion";
import { Button } from "@/components/Button/Button";
import { useSnackBar } from "@/utils/useSnackbar";
import { displayedDelayRows, FIRST_CONFIGURABLE_STEP } from "@/utils/stepDelayDefaults";

import { DraftReplies } from "@/enum/draft-replies.enum";
import { ButtonSize } from "@/components/Button/types";

import {
	UpsertCopywritingSettings,
	GreetingStyleEntry,
	AlternativePhrase,
	MessageLimitEntry,
	SendDelayEntry,
} from "../../schema/copywritingSettings";

import { DraftRepliesSection } from "./sections/DraftRepliesSection";
import { EmailSignatureSection } from "./sections/EmailSignatureSection";
import { SignOffStyleSection } from "./sections/SignOffStyleSection";
import { WordingBlacklistSection } from "./sections/WordingBlacklistSection";
import { CtaPoolSection } from "./sections/CtaPoolSection";
import { SubjectLinePatternsSection } from "./sections/SubjectLinePatternsSection";
import { GreetingStyleSection } from "./sections/GreetingStyleSection";
import { AlternativePhrasesSection } from "./sections/AlternativePhrasesSection";
import { MessageLimitsSection } from "./sections/MessageLimitsSection";
import { SendDelayDefaultsSection } from "./sections/SendDelayDefaultsSection";

const getBadge = (value: unknown): string | undefined => {
	if (Array.isArray(value) && value.length > 0) {
		return String(value.length);
	}

	if (typeof value === "string" && value.trim()) {
		return "✓";
	}

	return undefined;
};

const buildFormFromSettings = (
	settings: Partial<UpsertCopywritingSettings> | null
): UpsertCopywritingSettings => ({
	greetingStyle: settings?.greetingStyle ?? [],
	signOffStyle: settings?.signOffStyle ?? "",
	wordingBlacklist: settings?.wordingBlacklist ?? [],
	alternativePhrases: settings?.alternativePhrases ?? [],
	messageLimits: settings?.messageLimits ?? [],
	ctaPool: settings?.ctaPool ?? [],
	subjectLinePatterns: settings?.subjectLinePatterns ?? "",
	// PREC-4299: the rows the builders apply — the stored ones, or 1 day per step when none are.
	sendDelayDefaults: displayedDelayRows(settings?.sendDelayDefaults),
	// Always stored (PREC-4401); a missing value reads as on, as the API reads a missing row.
	appendEmailSignature: settings?.appendEmailSignature ?? true,
});

/**
 * The PATCH body. The step delays go only once the customer edited them: the page shows today's
 * 1-day default when nothing is stored, and saving another section must not store that (PREC-4299).
 */
const buildSavePayload = (
	form: UpsertCopywritingSettings,
	sendDelayEdited: boolean
): Partial<UpsertCopywritingSettings> => {
	const { sendDelayDefaults, ...rest } = form;

	if (!sendDelayEdited) {
		return rest;
	}

	return {
		...rest,
		sendDelayDefaults: (sendDelayDefaults ?? []).filter(
			(row) => row.step >= FIRST_CONFIGURABLE_STEP
		),
	};
};

export const CopywritingSettings: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`copywritingSettings.${key}`);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { isLoading } = useGetCopywritingSettingsQuery();
	const { settings } = useAppSelector(copywritingSettingsSelector);
	const { user } = useAppSelector(authSelector);
	const company = user?.company;
	const [patchSettings, { isLoading: isSaving }] = usePatchCopywritingSettingsMutation();
	const [updateCompany, { isLoading: isSavingCompany }] = useUpdateInSettingsMutation();

	const [form, setForm] = useState<UpsertCopywritingSettings>(() =>
		buildFormFromSettings(settings as Partial<UpsertCopywritingSettings> | null)
	);
	const [draftReplies, setDraftReplies] = useState<DraftReplies | null>(
		company?.draftReplies ?? null
	);
	const [isDirty, setIsDirty] = useState(false);
	const [sendDelayEdited, setSendDelayEdited] = useState(false);
	const footerRef = useRef<HTMLDivElement | null>(null);

	// Sync form when settings load from API (initial load only)
	useEffect(() => {
		if (settings && !isDirty) {
			setForm(buildFormFromSettings(settings as Partial<UpsertCopywritingSettings>));
		}
	}, [settings, isDirty]);

	// Sync draft replies from the company record while there are no pending edits
	useEffect(() => {
		if (!isDirty) {
			setDraftReplies(company?.draftReplies ?? null);
		}
	}, [company?.draftReplies, isDirty]);

	// Size the floating save bar to the scrollable content width (#outlet-container) so it spans
	// the content column without overlapping the sidebar. A callback ref applies the width the
	// moment the bar mounts — the bar renders only after the loading overlay clears, so an
	// effect keyed on [isDirty] would never re-run for that first mount and the bar would keep
	// its `w-auto` width (rendering as a small detached box). A resize listener keeps it in sync.
	const syncFooterWidth = useCallback((node: HTMLDivElement | null) => {
		footerRef.current = node;

		const outletElement = document.getElementById("outlet-container");

		if (node && outletElement) {
			node.style.setProperty("width", `${outletElement.offsetWidth}px`, "important");
		}
	}, []);

	useEffect(() => {
		const syncWidth = () => {
			const outletElement = document.getElementById("outlet-container");

			if (outletElement && footerRef.current) {
				footerRef.current.style.setProperty("width", `${outletElement.offsetWidth}px`, "important");
			}
		};

		window.addEventListener("resize", syncWidth);

		return () => window.removeEventListener("resize", syncWidth);
	}, []);

	const updateField = useCallback(
		<K extends keyof UpsertCopywritingSettings>(key: K, value: UpsertCopywritingSettings[K]) => {
			setForm((prev) => ({ ...prev, [key]: value }));
			setIsDirty(true);
		},
		[]
	);

	const updateSendDelays = useCallback(
		(value: SendDelayEntry[]) => {
			updateField("sendDelayDefaults", value);
			setSendDelayEdited(true);
		},
		[updateField]
	);

	const updateDraftReplies = useCallback((value: DraftReplies) => {
		setDraftReplies(value);
		setIsDirty(true);
	}, []);

	const handleSave = async () => {
		try {
			const tasks: Promise<unknown>[] = [
				patchSettings(buildSavePayload(form, sendDelayEdited)).unwrap(),
			];

			// Draft replies is a company-level setting; persist it alongside copywriting
			// settings so the single Save button saves both.
			if (draftReplies !== (company?.draftReplies ?? null)) {
				tasks.push(updateCompany({ name: company?.name ?? "", draftReplies }).unwrap());
			}

			await Promise.all(tasks);
			setIsDirty(false);
			setSendDelayEdited(false);
			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		}
	};

	const messageStyleItems: AccordionItem[] = useMemo(
		() => [
			{
				id: "greetingStyle",
				title: ts("greetingStyle.title"),
				badge: getBadge(form.greetingStyle),
				content: (
					<GreetingStyleSection
						value={form.greetingStyle as GreetingStyleEntry[]}
						onChange={(v) => updateField("greetingStyle", v)}
					/>
				),
			},
			{
				id: "signOffStyle",
				title: ts("signOffStyle.title"),
				badge: getBadge(form.signOffStyle),
				content: (
					<SignOffStyleSection
						value={form.signOffStyle ?? ""}
						onChange={(v) => updateField("signOffStyle", v)}
					/>
				),
			},
			{
				id: "emailSignature",
				title: ts("emailSignature.title"),
				badge: form.appendEmailSignature ? "✓" : undefined,
				content: (
					<EmailSignatureSection
						value={form.appendEmailSignature ?? true}
						onChange={(v) => updateField("appendEmailSignature", v)}
					/>
				),
			},
			{
				id: "subjectLinePatterns",
				title: ts("subjectLinePatterns.title"),
				badge: getBadge(form.subjectLinePatterns),
				content: (
					<SubjectLinePatternsSection
						value={form.subjectLinePatterns ?? ""}
						onChange={(v) => updateField("subjectLinePatterns", v)}
					/>
				),
			},
		],
		[form, ts, updateField]
	);

	const contentRulesItems: AccordionItem[] = useMemo(
		() => [
			{
				id: "wordingBlacklist",
				title: ts("wordingBlacklist.title"),
				badge: getBadge(form.wordingBlacklist),
				content: (
					<WordingBlacklistSection
						value={form.wordingBlacklist as string[]}
						onChange={(v) => updateField("wordingBlacklist", v)}
					/>
				),
			},
			{
				id: "alternativePhrases",
				title: ts("alternativePhrases.title"),
				badge: getBadge(form.alternativePhrases),
				content: (
					<AlternativePhrasesSection
						value={form.alternativePhrases as AlternativePhrase[]}
						onChange={(v) => updateField("alternativePhrases", v)}
					/>
				),
			},
			{
				id: "ctaPool",
				title: ts("ctaPool.title"),
				badge: getBadge(form.ctaPool),
				content: (
					<CtaPoolSection
						value={form.ctaPool as string[]}
						onChange={(v) => updateField("ctaPool", v)}
					/>
				),
			},
		],
		[form, ts, updateField]
	);

	const deliveryItems: AccordionItem[] = useMemo(
		() => [
			{
				id: "messageLimits",
				title: ts("messageLimits.title"),
				badge: getBadge(form.messageLimits),
				content: (
					<MessageLimitsSection
						value={form.messageLimits as MessageLimitEntry[]}
						onChange={(v) => updateField("messageLimits", v)}
					/>
				),
			},
			{
				id: "sendDelayDefaults",
				title: ts("sendDelayDefaults.title"),
				// Counts what is set, not the 1-day rows shown when nothing is.
				badge:
					settings?.sendDelayDefaults?.length || sendDelayEdited
						? getBadge(form.sendDelayDefaults)
						: undefined,
				content: (
					<SendDelayDefaultsSection
						value={form.sendDelayDefaults as SendDelayEntry[]}
						onChange={updateSendDelays}
					/>
				),
			},
		],
		[form, ts, updateField, updateSendDelays, settings?.sendDelayDefaults, sendDelayEdited]
	);

	if (isLoading) {
		return <LoadingOverlay />;
	}

	return (
		<div className="flex flex-col gap-6 py-4 pb-24">
			<div className="flex flex-col gap-1">
				<p className="text-sm text-text-meta">{ts("description")}</p>
			</div>

			{/* Draft Replies (company-level setting, saved with the shared Save button) */}
			<DraftRepliesSection value={draftReplies} onChange={updateDraftReplies} />

			{/* Message Style */}
			<div className="rounded-xl border border-border-light bg-brand-white p-4">
				<h3 className="text-smd font-medium text-text mb-1">{ts("group.messageStyle")}</h3>
				<p className="text-xs text-text-meta mb-2">{ts("group.messageStyleDescription")}</p>
				<Accordion allowMultiple items={messageStyleItems} />
			</div>

			{/* Content Rules */}
			<div className="rounded-xl border border-border-light bg-brand-white p-4">
				<h3 className="text-smd font-medium text-text mb-1">{ts("group.contentRules")}</h3>
				<p className="text-xs text-text-meta mb-2">{ts("group.contentRulesDescription")}</p>
				<Accordion allowMultiple items={contentRulesItems} />
			</div>

			{/* Delivery Settings */}
			<div className="rounded-xl border border-border-light bg-brand-white p-4">
				<h3 className="text-smd font-medium text-text mb-1">{ts("group.deliverySettings")}</h3>
				<p className="text-xs text-text-meta mb-2">{ts("group.deliverySettingsDescription")}</p>
				<Accordion allowMultiple items={deliveryItems} />
			</div>

			{/* Floating save bar */}
			<div
				ref={syncFooterWidth}
				className="fixed mt-14 flex flex-row justify-between items-center w-auto border-t border-t-border px-8 py-[14px] rounded-t-16 bottom-0 right-0 z-150 bg-white"
			>
				{isDirty ? (
					<span className="text-sm text-text-meta">{ts("unsavedChanges")}</span>
				) : (
					<>&nbsp;</>
				)}
				<div className="w-fit">
					<Button
						size={ButtonSize.S}
						isLoading={isSaving || isSavingCompany}
						disabled={!isDirty}
						title={ts("save")}
						type="button"
						onClick={handleSave}
					/>
				</div>
			</div>

			{renderMessageSnackbar()}
		</div>
	);
};
